import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

/** Rythme de relecture du compte pendant l'attente du webhook. */
export const CHECKOUT_POLL_MS = 2_000
/** Au-delà, l'écran dit que c'est plus long que prévu, et relit plus rarement. */
export const CHECKOUT_WAIT_MS = 60_000
/** Rythme de relecture une fois l'attente dépassée : le webhook finit par passer. */
export const CHECKOUT_SLOW_POLL_MS = 15_000

export type CheckoutReturnStatus = 'pending' | 'confirmed' | 'slow' | 'canceled'

type Options = {
  /** Le compte porte-t-il déjà un abonnement en cours ? */
  subscribed: boolean
  /** Relit le compte. */
  poll: () => Promise<unknown>
  /** Le reste de l'écran à rafraîchir une fois le palier activé. */
  onConfirmed: () => void
}

function initialStatus(outcome: string | null): CheckoutReturnStatus | null {
  if (outcome === 'success') return 'pending'
  if (outcome === 'canceled') return 'canceled'
  return null
}

/**
 * Le retour de Stripe Checkout (`?checkout=success|canceled`, #352). Revenir ne
 * prouve pas que le paiement est passé : le palier ne s'active que par le
 * webhook. On relit donc le compte jusqu'à voir l'abonnement, sans attendre
 * indéfiniment.
 */
export function useCheckoutReturn({ subscribed, poll, onConfirmed }: Options): CheckoutReturnStatus | null {
  const [params, setParams] = useSearchParams()
  const [outcome, setOutcome] = useState(() => initialStatus(params.get('checkout')))
  // L'abonnement vu sur le compte suffit à conclure : rien à mémoriser de plus.
  const waiting = outcome === 'pending' || outcome === 'slow'
  const status = waiting && subscribed ? 'confirmed' : outcome
  const latest = useRef({ poll, onConfirmed })
  latest.current = { poll, onConfirmed }

  // Le paramètre part une fois l'issue connue : recharger ne rejoue pas l'attente.
  const settled = status === 'confirmed' || status === 'canceled'
  useEffect(() => {
    if (!settled || !params.has('checkout')) return
    setParams((current) => {
      current.delete('checkout')
      return current
    }, { replace: true })
  }, [settled, params, setParams])

  useEffect(() => {
    if (status === 'confirmed') latest.current.onConfirmed()
  }, [status])

  useEffect(() => {
    if (status !== 'pending' && status !== 'slow') return
    // Un échec réseau n'interrompt rien : le prochain tour relit à nouveau.
    const poll = () => void latest.current.poll().catch(() => undefined)
    const interval = setInterval(poll, status === 'pending' ? CHECKOUT_POLL_MS : CHECKOUT_SLOW_POLL_MS)
    const timeout = status === 'pending' ? setTimeout(() => setOutcome('slow'), CHECKOUT_WAIT_MS) : undefined
    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [status])

  return status
}
